import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { WorkLog } from '../types';
import { getWorkLog } from '../services/api/workLog';
import { 
  ArrowLeftIcon, 
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { zhTW } from 'date-fns/locale';
import { useToast } from '../components/ToastContainer';
import MeetingDecisionReport from '../components/worklog/MeetingDecisionReport';

const WorkLogDetail: React.FC = () => {
  const { projectKey, workLogId } = useParams<{ projectKey: string; workLogId: string }>();
  const navigate = useNavigate();
  const { showError } = useToast();
  const [workLog, setWorkLog] = useState<WorkLog | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (workLogId) {
      loadWorkLog();
    }
  }, [workLogId]);

  const loadWorkLog = async () => {
    try {
      setIsLoading(true);
      const data = await getWorkLog(workLogId!);
      setWorkLog(data);
    } catch (error) {
      console.error('Failed to load work log:', error);
      showError('載入工作日誌失敗');
      navigate(`/project/${projectKey}/worklogs`);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800';
      case 'PUBLISHED':
        return 'bg-green-100 text-green-800';
      case 'ARCHIVED':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return '草稿';
      case 'PUBLISHED':
        return '已發布';
      case 'ARCHIVED':
        return '已封存';
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!workLog) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">工作日誌不存在</h3>
        <p className="text-gray-500">請檢查連結是否正確</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* 頁面標題 */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <button
            onClick={() => navigate(`/project/${projectKey}/worklogs`)}
            className="flex items-center text-gray-600 hover:text-gray-800 mr-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            返回列表
          </button>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-2">
                <DocumentTextIcon className="h-6 w-6 text-blue-600 mr-2" />
                <h1 className="text-2xl font-bold text-gray-900">{workLog.title}</h1>
                <span className={`ml-3 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(workLog.status)}`}>
                  {getStatusText(workLog.status)}
                </span>
              </div>
              
            </div>
          </div>
        </div>
      </div>

      {/* 整合的內容區域 */}
      <div className="space-y-6">
        {/* 基本資訊區塊 */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-medium mb-4">基本資訊</h3>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="space-y-3">
                <div>
                  <span className="text-gray-600">工作期間：</span>
                  <span className="font-medium">
                    {format(new Date(workLog.startDate), 'yyyy-MM-dd', { locale: zhTW })} ~ {format(new Date(workLog.endDate), 'yyyy-MM-dd', { locale: zhTW })}
                  </span>
                </div>
                {workLog.communityName && (
                  <div>
                    <span className="text-gray-600">社區名稱：</span>
                    <span className="font-medium">{workLog.communityName}</span>
                  </div>
                )}
                {workLog.managementCompany && (
                  <div>
                    <span className="text-gray-600">管理公司：</span>
                    <span className="font-medium">{workLog.managementCompany}</span>
                  </div>
                )}
                {workLog.dutyStaff && (
                  <div>
                    <span className="text-gray-600">值勤人員：</span>
                    <span className="font-medium">{workLog.dutyStaff}</span>
                  </div>
                )}
                <div>
                  <span className="text-gray-600">建立者：</span>
                  <span className="font-medium">{workLog.createdBy?.name}</span>
                </div>
                <div>
                  <span className="text-gray-600">建立時間：</span>
                  <span className="font-medium">
                    {format(new Date(workLog.createdAt), 'yyyy-MM-dd HH:mm:ss', { locale: zhTW })}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">最後更新：</span>
                  <span className="font-medium">
                    {format(new Date(workLog.updatedAt), 'yyyy-MM-dd HH:mm:ss', { locale: zhTW })}
                  </span>
                </div>
              </div>
            </div>

            {/* 統計資訊 */}
            <div>
              <h4 className="text-md font-medium mb-3">統計資訊</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">關聯議題：</span>
                  <span className="font-medium text-blue-600">
                    {workLog.selectedIssues?.length || 0} 個
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 關聯議題列表 */}
          {workLog.selectedIssues && workLog.selectedIssues.length > 0 && (
            <div className="mt-6">
              <h4 className="text-md font-medium mb-4">關聯議題</h4>
              <div className="space-y-2">
                {workLog.selectedIssues.map((workLogIssue) => (
                  <div
                    key={workLogIssue.id}
                    className="flex items-center justify-between p-3 border rounded-md hover:bg-gray-50"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-500">
                          {workLogIssue.issue.key}
                        </span>
                        <span className="font-medium">{workLogIssue.issue.title}</span>
                      </div>
                      {workLogIssue.issue.assignee && (
                        <div className="text-sm text-gray-600 mt-1">
                          負責人：{workLogIssue.issue.assignee.name}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center">
                      {workLogIssue.issue.boardStatus ? (
                        <span
                          className="px-2 py-1 text-xs font-medium rounded-full text-white"
                          style={{ backgroundColor: workLogIssue.issue.boardStatus.color }}
                        >
                          {workLogIssue.issue.boardStatus.name}
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                          {workLogIssue.issue.status}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 工作執行詳情區塊 */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-medium">工作執行詳情</h3>
          </div>
          <MeetingDecisionReport workLogId={workLog.id} />
        </div>

        {/* 工作項目內容區塊 */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-medium mb-4">工作項目內容</h3>
          <div className="space-y-6">
            {/* 上午工作事項 */}
            {workLog.morningWorkItems && (
              <div>
                <h4 className="text-md font-medium mb-3">上午工作事項</h4>
                <div className="bg-gray-50 p-4 rounded-md">
                  <pre className="text-sm whitespace-pre-wrap font-sans">
                    {workLog.morningWorkItems}
                  </pre>
                </div>
              </div>
            )}

            {/* 下午工作事項 */}
            {workLog.afternoonWorkItems && (
              <div>
                <h4 className="text-md font-medium mb-3">下午工作事項</h4>
                <div className="bg-gray-50 p-4 rounded-md">
                  <pre className="text-sm whitespace-pre-wrap font-sans">
                    {workLog.afternoonWorkItems}
                  </pre>
                </div>
              </div>
            )}

            {/* 其他工作事項 */}
            {workLog.otherWorkItems && (
              <div>
                <h4 className="text-md font-medium mb-3">其他工作事項</h4>
                <div className="bg-gray-50 p-4 rounded-md">
                  <pre className="text-sm whitespace-pre-wrap font-sans">
                    {workLog.otherWorkItems}
                  </pre>
                </div>
              </div>
            )}

            {!workLog.morningWorkItems && !workLog.afternoonWorkItems && !workLog.otherWorkItems && (
              <div className="text-center py-8 text-gray-500">
                尚未填寫工作項目內容
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkLogDetail;
